import {
  Clock, Globe, BookOpen, Award, FileText,
  Target, Shield, Layers, Zap, Monitor,
  Calendar, Languages, ClipboardCheck
} from 'lucide-react';

export const quickInfo = [
  { icon: BookOpen, label: 'Course', value: 'AI in Humanitarian Action & Development' },
  { icon: Clock, label: 'Duration', value: '4 Weeks' },
  { icon: Globe, label: 'Delivery', value: '100% Online and Self-Paced' },
  { icon: Calendar, label: 'Access', value: '12 Months' },
  { icon: Languages, label: 'Language', value: 'English' },
  { icon: ClipboardCheck, label: 'Assessment', value: 'One Final Practical Assignment' },
  { icon: FileText, label: 'Resource', value: 'Humanitarian & Development AI Prompt Bank' },
  { icon: Award, label: 'Certificate', value: 'Certificate of Completion' },
];

export const heroBadges = [
  '4-Week Online Course',
  'Self-Paced Learning',
  '200+ Practical AI Prompts',
  'Certificate of Completion',
  'No Technical Experience Required'
];

export const audienceList = [
  'Humanitarian professionals',
  'Development practitioners',
  'NGO staff',
  'Local NGO founders and leaders',
  'Project officers',
  'Program officers',
  'Program managers',
  'Field coordinators and field managers',
  'Grants and proposal-development staff',
  'Business development staff',
  'MEAL professionals',
  'Monitoring and evaluation staff',
  'Reporting officers',
  'Partnership and localization staff',
  'Communications staff',
  'Human resources and operations staff',
  'Consultants working with NGOs',
  'Students and early-career professionals entering the sector'
];

export const learningOutcomes = [
  'Understand the major categories of AI tools available for professional work',
  'Select an appropriate AI tool for a particular humanitarian or development task',
  'Write clear, structured, and context-specific prompts',
  'Improve AI responses through follow-up instructions and iterative prompting',
  'Provide AI with appropriate background information, requirements, and constraints',
  'Review AI-generated content for accuracy, relevance, logic, bias, and completeness',
  'Use AI to support NGO strategy and organizational development',
  'Use AI to support context analysis and needs-assessment planning',
  'Use AI to support humanitarian and development program design',
  'Use AI to analyze donor priorities and funding opportunities',
  'Use AI to support concept-note and proposal development',
  'Use AI to support implementation planning and field coordination',
  'Use AI to support MEAL, reporting, learning, and accountability work',
  'Use AI to improve professional communication and daily productivity',
  'Recognize tasks and information that should not be given to public AI systems',
  'Maintain human oversight and professional responsibility when using AI'
];

export const modules = [
  {
    number: 1,
    title: 'Understanding AI Tools for Humanitarian and Development Work',
    overview: 'This module introduces the main categories of AI tools and explains where they can support humanitarian, development, and NGO work.',
    topics: [
      'What artificial intelligence is',
      'What generative AI can and cannot do',
      'AI assistants and conversational tools',
      'AI-powered research tools',
      'AI tools for documents and long reports',
      'AI tools for presentations and visual content',
      'AI tools for spreadsheets and data analysis',
      'AI tools for translation and language support',
      'AI tools for meetings and productivity',
      'Choosing the right tool for the right task',
      'Free and paid AI tool options',
      'Limitations of AI systems',
      'Why human review remains essential'
    ],
    practicalFocus: 'Learners examine different professional tasks and identify which category of AI tool could support each task.'
  },
  {
    number: 2,
    title: 'Prompting Skills and Efficient AI Workflows',
    overview: 'This module teaches learners how to communicate with AI effectively and how to improve weak or generic responses.',
    topics: [
      'The components of a strong prompt',
      'Defining the role of the AI',
      'Explaining the task clearly',
      'Providing context and background',
      'Adding objectives and intended users',
      'Specifying constraints',
      'Defining tone and level of detail',
      'Requesting a useful output structure',
      'Providing examples and reference information',
      'Breaking complex tasks into stages',
      'Asking AI to identify missing information',
      'Using follow-up prompts',
      'Reviewing and improving the first response',
      'Comparing alternative outputs',
      'Building repeatable AI workflows',
      'Avoiding vague and one-line prompting'
    ],
    practicalFocus: 'Learners move from a weak prompt to a detailed professional prompt and compare the resulting outputs.'
  },
  {
    number: 3,
    title: 'AI for NGO Strategy and Organizational Development',
    overview: 'This module shows how AI can support the development and review of an NGO\'s identity, strategic direction, positioning, and organizational documentation.',
    topics: [
      'Developing and refining mission and vision statements',
      'Defining organizational values',
      'Clarifying program areas',
      'Developing organizational profiles',
      'Developing capability statements',
      'Conducting SWOT analysis',
      'Mapping stakeholders',
      'Mapping potential partners',
      'Identifying strategic priorities',
      'Supporting annual planning',
      'Reviewing organizational strengths and gaps',
      'Supporting organizational risk analysis',
      'Preparing internal strategic discussions',
      'Improving institutional communication'
    ],
    practicalFocus: 'Learners use AI to convert incomplete organizational information into a structured organizational profile and strategic discussion.'
  },
  {
    number: 4,
    title: 'AI for Needs Assessment and Context Analysis',
    overview: 'This module teaches learners how AI can support assessment planning and contextual analysis without replacing primary data collection or field evidence.',
    topics: [
      'Structuring a context analysis',
      'Planning a secondary-data review',
      'Identifying information gaps',
      'Developing problem statements',
      'Supporting problem-tree analysis',
      'Developing key-informant interview questions',
      'Developing focus-group discussion questions',
      'Drafting survey questions',
      'Supporting stakeholder analysis',
      'Supporting vulnerability analysis',
      'Considering gender, age, disability, and inclusion',
      'Supporting protection-sensitive analysis',
      'Supporting conflict-sensitive analysis',
      'Summarizing collected information',
      'Comparing findings from different sources',
      'Identifying contradictions and missing evidence'
    ],
    practicalFocus: 'Learners use AI to develop an assessment approach and improve the quality of questions for a humanitarian or development context.',
    importantNote: 'AI may support the structure and analysis of assessment work, but it must not invent community information, beneficiary numbers, field findings, quotations, or evidence.'
  },
  {
    number: 5,
    title: 'AI for Program and Project Design',
    overview: 'This module explains how AI can support the development of a structured program or project from an identified problem.',
    topics: [
      'Converting a problem into a project concept',
      'Developing a problem tree',
      'Developing an objective tree',
      'Supporting Theory of Change development',
      'Defining a project goal',
      'Developing project objectives',
      'Distinguishing outputs, outcomes, and impact',
      'Developing activities',
      'Identifying assumptions',
      'Defining target groups',
      'Developing an implementation approach',
      'Considering gender, protection, inclusion, and localization',
      'Supporting risk identification',
      'Supporting risk-mitigation planning',
      'Developing sustainability considerations',
      'Reviewing project logic',
      'Identifying gaps and inconsistencies in program design'
    ],
    practicalFocus: 'Learners use AI to move from a humanitarian or development problem to a structured project concept.'
  },
  {
    number: 6,
    title: 'AI for Donor Mapping, Funding Opportunities, and Proposal Development',
    overview: 'This module teaches learners how AI can support donor research, funding-opportunity analysis, concept-note development, and proposal review.',
    topics: [
      'Understanding different donor categories',
      'Researching donor priorities',
      'Comparing donors and funding mechanisms',
      'Developing a donor-mapping approach',
      'Reviewing a funding announcement',
      'Extracting eligibility requirements',
      'Extracting submission requirements',
      'Identifying deadlines and key conditions',
      'Summarizing evaluation criteria',
      'Comparing an opportunity with organizational capacity',
      'Supporting go/no-go decisions',
      'Developing a concept-note structure',
      'Improving a problem statement',
      'Developing objectives, results, and activities',
      'Supporting proposal narrative development',
      'Supporting risk and sustainability sections',
      'Supporting a budget narrative',
      'Reviewing proposal consistency',
      'Identifying weak proposal sections',
      'Improving clarity and donor alignment',
      'Preparing professional donor communication'
    ],
    practicalFocus: 'Learners use AI to analyze a fictional funding opportunity and develop a structured proposal-development workflow.',
    importantNote: 'AI cannot guarantee grant approval, funding, donor acceptance, or proposal success. It can support research, analysis, drafting, review, and improvement.'
  },
  {
    number: 7,
    title: 'AI for Program Implementation, Field Coordination, and MEAL',
    overview: 'This module demonstrates how AI can support the operational phase of a project after design or funding.',
    topics: [
      'Developing implementation workplans',
      'Breaking activities into operational tasks',
      'Supporting activity scheduling',
      'Clarifying team roles and responsibilities',
      'Supporting field-visit planning',
      'Supporting training and workshop planning',
      'Developing meeting agendas',
      'Preparing coordination updates',
      'Extracting action points from notes',
      'Supporting issue and risk tracking',
      'Supporting standard operating procedure development',
      'Supporting monitoring plans',
      'Reviewing indicators',
      'Developing monitoring questions',
      'Supporting data-collection planning',
      'Summarizing qualitative information',
      'Supporting feedback and accountability analysis',
      'Supporting after-action reviews',
      'Supporting lessons-learned processes'
    ],
    practicalFocus: 'Learners use AI to turn a project activity into an implementation, monitoring, and coordination workflow.',
    importantNote: 'Sensitive personal, beneficiary, employee, financial, protection, medical, and security information should not be entered into public AI tools without proper authorization and protection.'
  },
  {
    number: 8,
    title: 'AI for Reporting, Communication, and Professional Productivity',
    overview: 'This module shows how AI can support reporting, communication, documentation, and repetitive daily work.',
    topics: [
      'Drafting progress reports',
      'Improving donor reports',
      'Summarizing implementation updates',
      'Developing situation reports',
      'Drafting activity reports',
      'Developing success stories',
      'Developing case studies',
      'Summarizing lessons learned',
      'Improving executive summaries',
      'Drafting professional emails',
      'Preparing meeting minutes',
      'Extracting decisions and action points',
      'Developing briefing notes',
      'Supporting internal memos',
      'Supporting presentation outlines',
      'Simplifying technical information',
      'Translating and localizing professional content',
      'Reviewing writing for clarity and tone',
      'Planning professional tasks',
      'Developing repeatable personal AI workflows',
      'Maintaining quality control and human responsibility'
    ],
    practicalFocus: 'Learners use AI to improve a weak report or professional communication while checking facts, tone, and contextual accuracy.'
  }
];

export const promptBankCategories = [
  { title: 'AI Tool Selection and Prompt Improvement', desc: 'Prompts for selecting an appropriate tool, improving weak instructions, identifying missing information, and reviewing AI responses.' },
  { title: 'NGO Strategy and Organizational Development', desc: 'Prompts for mission, vision, organizational positioning, capability analysis, stakeholder mapping, partnerships, strategic priorities, and institutional communication.' },
  { title: 'Context Analysis and Needs Assessment', desc: 'Prompts for assessment planning, secondary-data review, problem analysis, stakeholder analysis, interview questions, focus groups, surveys, vulnerability, inclusion, and conflict sensitivity.' },
  { title: 'Program and Project Design', desc: 'Prompts for problem trees, objective trees, Theory of Change, goals, objectives, outcomes, outputs, activities, risks, assumptions, sustainability, and implementation logic.' },
  { title: 'Donor Mapping and Funding Analysis', desc: 'Prompts for donor research, donor-priority analysis, funding-call review, eligibility analysis, requirement extraction, opportunity comparison, and go/no-go decision support.' },
  { title: 'Concept Notes and Proposal Development', desc: 'Prompts for concept notes, problem statements, objectives, results, activities, project logic, MEAL sections, risk sections, sustainability, budget narratives, and proposal review.' },
  { title: 'Program Implementation and Field Coordination', desc: 'Prompts for workplans, activity planning, scheduling, field visits, team responsibilities, coordination, meetings, action points, issue tracking, and operational planning.' },
  { title: 'MEAL, Accountability, and Learning', desc: 'Prompts for indicator review, monitoring questions, data-collection planning, feedback analysis, qualitative summaries, after-action reviews, evaluation questions, and lessons learned.' },
  { title: 'Reporting and Professional Communication', desc: 'Prompts for progress reports, donor reports, situation reports, activity reports, case studies, success stories, executive summaries, emails, meeting minutes, briefing notes, and presentations.' },
  { title: 'Responsible AI Review', desc: 'Prompts for checking factual accuracy, identifying unsupported claims, detecting missing context, reviewing potential bias, protecting confidentiality, and ensuring professional human oversight.' }
];

export const howItWorks = [
  { step: 1, title: 'Understand the Tool', desc: 'Learn what different AI tool categories can do and where each one may support humanitarian and development work.' },
  { step: 2, title: 'Understand the Task', desc: 'Identify the professional objective, intended audience, required information, constraints, risks, and expected output.' },
  { step: 3, title: 'Build the Prompt', desc: 'Provide the AI with a role, task, context, objective, requirements, limitations, and expected structure.' },
  { step: 4, title: 'Review the First Response', desc: 'Check whether the response is accurate, complete, relevant, contextualized, and professionally appropriate.' },
  { step: 5, title: 'Improve the Output', desc: 'Use follow-up prompts to correct weaknesses, add missing information, compare alternatives, and strengthen the response.' },
  { step: 6, title: 'Apply Human Judgment', desc: 'Verify facts, protect sensitive information, adapt the output to the real context, and take professional responsibility for the final work.' }
];

export const whatIsIncluded = [
  'Access to the complete 4-week online course',
  'Eight practical learning modules',
  'Step-by-step AI demonstrations',
  'Lessons on major categories of AI tools',
  'Prompt-writing and prompt-improvement techniques',
  'Humanitarian and development sector use cases',
  'Responsible AI and confidentiality guidance',
  'Humanitarian & Development AI Prompt Bank',
  'More than 200 reusable AI prompts',
  'One final practical assignment',
  'Certificate of Completion',
  '12 months of course access'
];

export const responsibleAIPoints = [
  'Verify factual claims',
  'Review sources',
  'Avoid invented field evidence',
  'Protect confidential information',
  'Remove personal and identifying data',
  'Avoid entering sensitive beneficiary information into unauthorized systems',
  'Review outputs for bias and exclusion',
  'Adapt language to the local context',
  'Apply organizational policies',
  'Maintain human responsibility for final decisions and documents'
];

export const featureCards = [
  { icon: Target, title: 'Sector-Specific Learning', desc: 'The lessons are built around NGO, humanitarian, development, grants, project, MEAL, reporting, and field-management responsibilities.' },
  { icon: Layers, title: 'Full Program-Cycle Coverage', desc: 'The course covers the process from organizational positioning and needs assessment through program design, funding, implementation, monitoring, reporting, and learning.' },
  { icon: Zap, title: 'Repeatable Skills', desc: 'Learners are taught how to create and improve their own AI workflows instead of depending on one fixed format.' },
  { icon: Monitor, title: 'Practical Demonstrations', desc: 'Lessons show how a professional task can be approached, prompted, reviewed, corrected, and finalized with AI support.' },
  { icon: Shield, title: 'Responsible AI Use', desc: 'The course emphasizes human judgment, confidentiality, verification, contextual adaptation, and ethical professional use.' },
  { icon: FileText, title: 'Reusable Prompt Bank', desc: 'Learners receive a sector-specific prompt bank containing more than 200 practical prompts for humanitarian and development work.' }
];

export const faqs = [
  {
    question: 'What is AI in Humanitarian Action & Development?',
    answer: 'It is a short professional online course that teaches humanitarian, development, and NGO professionals how to use AI tools responsibly across organizational strategy, needs assessment, program design, donor mapping, proposal development, implementation, MEAL, reporting, and professional productivity.'
  },
  {
    question: 'Is this a technical AI or coding course?',
    answer: 'No. The course does not teach coding, machine learning, software development, or AI engineering. It teaches professionals how to use existing AI tools in their work.'
  },
  {
    question: 'Do I need previous AI experience?',
    answer: 'No. The course begins with AI tools, basic concepts, tool selection, and effective prompting. It is suitable for beginners as well as professionals already using AI informally.'
  },
  {
    question: 'Do I need a paid AI subscription?',
    answer: 'Not necessarily. Learners can follow the main learning process using commonly available AI tools. Some tools or advanced features may require separate paid subscriptions, but purchasing those tools is not included in the course fee and is not required for basic participation.'
  },
  {
    question: 'Which AI tools will be covered?',
    answer: 'The course explains major categories of AI tools, including general AI assistants, AI research tools, document-analysis tools, presentation tools, spreadsheet tools, translation tools, and productivity tools. Specific platforms may be demonstrated as examples, but the course focuses on transferable skills that remain useful when tools and platforms change.'
  },
  {
    question: 'Will I receive templates?',
    answer: 'No. The purpose of the course is to teach you how to use AI to create, review, improve, and adapt outputs for different situations. You will receive a Humanitarian & Development AI Prompt Bank containing more than 200 reusable prompts.'
  },
  {
    question: 'What is the AI Prompt Bank?',
    answer: 'The Humanitarian & Development AI Prompt Bank is a collection of more than 200 practical prompts covering NGO strategy, needs assessment, program design, donor mapping, proposal development, implementation, MEAL, reporting, communication, and responsible AI review. The prompts can be customized for different organizations, countries, sectors, donors, and projects.'
  },
  {
    question: 'Does the course guarantee donor funding?',
    answer: 'No. No training provider or AI tool can guarantee donor funding. The course teaches how AI may support donor research, funding-opportunity analysis, proposal drafting, review, and improvement. Final funding decisions remain with donors.'
  },
  {
    question: 'Can AI write a complete proposal for me?',
    answer: 'AI can support parts of the proposal-development process, but the organization remains responsible for evidence, technical quality, accuracy, budgets, commitments, compliance, contextual relevance, and final approval. AI-generated content must be reviewed and adapted by qualified professionals.'
  },
  {
    question: 'Can I upload beneficiary or organizational data into AI tools?',
    answer: 'Sensitive information should not be entered into public or unauthorized AI systems. The course explains the importance of confidentiality, anonymization, organizational authorization, data protection, and professional responsibility.'
  },
  {
    question: 'Is the course self-paced?',
    answer: 'Yes. The course is delivered online and can be completed according to the learner\'s schedule within the access period.'
  },
  {
    question: 'How long will I have access?',
    answer: 'Learners receive 12 months of access from the date their course account is activated.'
  },
  {
    question: 'How long does the course take to complete?',
    answer: 'The recommended duration is four weeks, with approximately two to three hours of study per week. Learners may progress according to their own schedule during the access period.'
  },
  {
    question: 'Is there an assignment?',
    answer: 'Yes. Learners complete one practical assignment demonstrating how they used AI to support and improve a humanitarian, development, NGO, or professional task.'
  },
  {
    question: 'Will I receive a certificate?',
    answer: 'Yes. Learners who complete the course requirements and final practical assignment receive a Certificate of Completion.'
  },
  {
    question: 'Is this an academic degree?',
    answer: 'No. This is a short professional course. It does not lead to a master\'s degree, doctoral degree, professional license, or regulated qualification.'
  },
  {
    question: 'Who is eligible to enroll?',
    answer: 'The course is open to humanitarian and development professionals, NGO staff, local NGO leaders, project and program staff, MEAL professionals, grants staff, consultants, students, and others interested in the sector.'
  },
  {
    question: 'What is the course language?',
    answer: 'The course is delivered in English.'
  },
  {
    question: 'What equipment do I need?',
    answer: 'Learners need a computer, tablet, or capable smartphone, a reliable internet connection, an email address, and access to at least one commonly available AI tool. A computer is recommended for completing longer professional exercises.'
  },
  {
    question: 'When can I start?',
    answer: 'Learners can begin after payment confirmation and course-account activation.'
  }
];
